import React, { useState } from 'react';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const InteractiveMap = () => {
  const [mapType, setMapType] = useState('roadmap');
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Resort coordinates (Dandeli, Karnataka)
  const resortLat = 15.2593;
  const resortLng = 74.6167;

  const mapTypes = [
    { id: 'roadmap', label: 'Road Map', icon: 'Map' },
    { id: 'satellite', label: 'Satellite', icon: 'Satellite' },
    { id: 'terrain', label: 'Terrain', icon: 'Mountain' }
  ];

  const popularRoutes = [
    {
      id: 'bangalore',
      city: 'Bangalore',
      distance: '485 km',
      duration: '8-9 hours',
      description: 'Via NH48 and NH4A - Most popular route'
    },
    {
      id: 'mumbai',
      city: 'Mumbai',
      distance: '520 km',
      duration: '9-10 hours',
      description: 'Via NH66 and SH34 - Coastal route'
    },
    {
      id: 'pune',
      city: 'Pune',
      distance: '380 km',
      duration: '7-8 hours',
      description: 'Via NH4 and SH34 - Scenic route'
    },
    {
      id: 'goa',
      city: 'Goa',
      distance: '120 km',
      duration: '2.5-3 hours',
      description: 'Via NH4A - Shortest route'
    }
  ];

  const nearbyLandmarks = [
    { name: 'Dandeli Wildlife Sanctuary', distance: '5 km', icon: 'Trees' },
    { name: 'Syntheri Rocks', distance: '8 km', icon: 'Mountain' },
    { name: 'Kali River', distance: '2 km', icon: 'Waves' },
    { name: 'Supa Dam', distance: '12 km', icon: 'Zap' },
    { name: 'Dandeli Railway Station', distance: '15 km', icon: 'Train' },
    { name: 'Hubli Airport', distance: '75 km', icon: 'Plane' }
  ];

  const getMapUrl = () => {
    const baseUrl = 'https://www.google.com/maps/embed/v1/place';
    const apiKey = 'YOUR_API_KEY'; // In production, use environment variable
    return `https://www.google.com/maps?q=${resortLat},${resortLng}&z=14&t=${mapType}&output=embed`;
  };

  const handleDirections = (fromCity) => {
    const directionsUrl = `https://www.google.com/maps/dir/${fromCity}/Dandeli+Adventure+Resorts,+Dandeli,+Karnataka`;
    window.open(directionsUrl, '_blank');
  };

  const handleGetDirections = () => {
    const directionsUrl = `https://www.google.com/maps/dir//Dandeli+Adventure+Resorts,+Kali+River+Road,+Dandeli,+Karnataka+581325`;
    window.open(directionsUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Map Container */}
      <div className="bg-card rounded-lg shadow-organic overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="MapPin" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-semibold text-card-foreground">
                  Resort Location
                </h2>
                <p className="text-sm text-muted-foreground">
                  Interactive map with directions
                </p>
              </div>
            </div>

            <div className="flex space-x-2">
              {mapTypes?.map((type) => (
                <Button
                  key={type?.id}
                  variant={mapType === type?.id ? 'default' : 'outline'}
                  size="sm"
                  iconName={type?.icon}
                  iconPosition="left"
                  onClick={() => setMapType(type?.id)}
                >
                  {type?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="relative">
          <iframe
            width="100%"
            height="400"
            loading="lazy"
            title="Dandeli Adventure Resorts Location"
            referrerPolicy="no-referrer-when-downgrade"
            src={getMapUrl()}
            className="border-0"
          />
          
          <div className="absolute bottom-4 right-4">
            <Button
              variant="default"
              size="sm"
              iconName="Navigation"
              iconPosition="left"
              onClick={handleGetDirections}
            >
              Get Directions
            </Button>
          </div>
        </div>
      </div>
      {/* Popular Routes */}
      <div className="bg-card rounded-lg shadow-organic p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Route" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-card-foreground">
              Popular Routes
            </h3>
            <p className="text-sm text-muted-foreground">
              Driving directions from major cities
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {popularRoutes?.map((route) => (
            <div
              key={route?.id}
              className={`p-4 border rounded-lg cursor-pointer transition-organic ${
                selectedRoute === route?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-primary/5'
              }`}
              onClick={() => setSelectedRoute(route?.id)}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-medium text-card-foreground">
                    From {route?.city}
                  </h4>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm text-muted-foreground flex items-center">
                      <Icon name="MapPin" size={14} className="mr-1" />
                      {route?.distance}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center">
                      <Icon name="Clock" size={14} className="mr-1" />
                      {route?.duration}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Navigation"
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleDirections(route?.city);
                  }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {route?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
      {/* Nearby Landmarks */}
      <div className="bg-card rounded-lg shadow-organic p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Compass" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-card-foreground">
              Nearby Landmarks
            </h3>
            <p className="text-sm text-muted-foreground">
              Points of interest around the resort
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {nearbyLandmarks?.map((landmark, index) => (
            <div
              key={index}
              className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-organic"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                <Icon name={landmark?.icon} size={16} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-card-foreground text-sm">
                  {landmark?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {landmark?.distance} away
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Transportation Info */}
      <div className="bg-card rounded-lg shadow-organic p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Car" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-card-foreground">
              Transportation Options
            </h3>
            <p className="text-sm text-muted-foreground">
              How to reach Dandeli Adventure Resorts
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-3">
              <Icon name="Car" size={24} className="text-primary" />
            </div>
            <h4 className="font-medium text-card-foreground mb-2">By Car</h4>
            <p className="text-sm text-muted-foreground">
              Self-drive or taxi from major cities. Free parking available at the resort.
            </p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-3">
              <Icon name="Train" size={24} className="text-primary" />
            </div>
            <h4 className="font-medium text-card-foreground mb-2">By Train</h4>
            <p className="text-sm text-muted-foreground">
              Nearest station: Dandeli Road (15 km). We provide pickup service on request.
            </p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-3">
              <Icon name="Plane" size={24} className="text-primary" />
            </div>
            <h4 className="font-medium text-card-foreground mb-2">By Air</h4>
            <p className="text-sm text-muted-foreground">
              Nearest airport: Hubli (75 km). Airport transfer can be arranged.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;